/**
 * Faixa de serviços ao vivo do cabeçalho. Cada item aponta para uma página do
 * próprio site, para uma categoria ou para um endereço externo (https).
 */
import { Button } from '@/components/atoms';
import type { AlvoFaixa, ErroDeCampo, ItemFaixa } from '@/types/portal';

import { CampoSelecao, CampoTexto } from './campos';
import { erroDe, mover, remover, trocar } from './helpers';
import styles from './Portal.module.css';

type TipoAlvo = 'caminho' | 'categoria' | 'url';

const TIPOS: { valor: TipoAlvo; rotulo: string }[] = [
  { valor: 'caminho', rotulo: 'Página do site' },
  { valor: 'categoria', rotulo: 'Categoria' },
  { valor: 'url', rotulo: 'Endereço externo' },
];

function tipoDoAlvo(alvo: AlvoFaixa): TipoAlvo {
  if ('caminho' in alvo) return 'caminho';
  if ('categoria' in alvo) return 'categoria';
  return 'url';
}

function valorDoAlvo(alvo: AlvoFaixa): string {
  if ('caminho' in alvo) return alvo.caminho;
  if ('categoria' in alvo) return alvo.categoria;
  return alvo.url;
}

function alvoDe(tipo: TipoAlvo, valor: string): AlvoFaixa {
  if (tipo === 'caminho') return { caminho: valor };
  if (tipo === 'categoria') return { categoria: valor };
  return { url: valor };
}

const PLACEHOLDER: Record<TipoAlvo, string> = {
  caminho: 'previsao-do-tempo/',
  categoria: 'esportes',
  url: 'https://exemplo.org/pagina',
};

interface Props {
  itens: ItemFaixa[];
  onChange: (itens: ItemFaixa[]) => void;
  erros: ErroDeCampo[];
  campo: string;
}

export function EditorFaixa({ itens, onChange, erros, campo }: Props) {
  const erroLista = erroDe(erros, campo);
  return (
    <div>
      <h4 className={styles.sub2}>Faixa de serviços do cabeçalho ({itens.length})</h4>
      {itens.length === 0 ? (
        <p className={styles.vazio}>Sem itens: a faixa não aparece.</p>
      ) : (
        <ol className={styles.lista} aria-label="Faixa de serviços">
          {itens.map((it, i) => {
            const tipo = tipoDoAlvo(it.alvo);
            return (
              <li key={i} className={styles.linha}>
                <div className={styles.linhaCampos}>
                  <CampoTexto
                    rotulo={`Chave do item ${i + 1}`}
                    valor={it.chave}
                    mono
                    maxLength={80}
                    erro={erroDe(erros, `${campo}.${i}.chave`)}
                    onChange={(chave) => onChange(trocar(itens, i, { ...it, chave }))}
                  />
                  <CampoTexto
                    rotulo={`Rótulo do item ${i + 1}`}
                    valor={it.rotulo}
                    maxLength={30}
                    erro={erroDe(erros, `${campo}.${i}.rotulo`)}
                    onChange={(rotulo) => onChange(trocar(itens, i, { ...it, rotulo }))}
                  />
                  <CampoSelecao
                    rotulo={`Destino do item ${i + 1}`}
                    valor={tipo}
                    opcoes={TIPOS}
                    onChange={(t) => onChange(trocar(itens, i, { ...it, alvo: alvoDe(t, '') }))}
                  />
                  <CampoTexto
                    rotulo={`Endereço do item ${i + 1}`}
                    valor={valorDoAlvo(it.alvo)}
                    mono
                    placeholder={PLACEHOLDER[tipo]}
                    erro={erroDe(erros, `${campo}.${i}.alvo.${tipo}`)}
                    onChange={(v) => onChange(trocar(itens, i, { ...it, alvo: alvoDe(tipo, v) }))}
                  />
                </div>
                <div className={styles.linhaAcoes}>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={i === 0}
                    onClick={() => onChange(mover(itens, i, i - 1))}
                    aria-label={`Subir item ${i + 1}`}
                  >
                    Subir
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={i === itens.length - 1}
                    onClick={() => onChange(mover(itens, i, i + 1))}
                    aria-label={`Descer item ${i + 1}`}
                  >
                    Descer
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => onChange(remover(itens, i))}
                    aria-label={`Remover item ${i + 1}`}
                  >
                    Remover
                  </Button>
                </div>
              </li>
            );
          })}
        </ol>
      )}
      {erroLista && <span className={styles.erroCampo}>{erroLista}</span>}
      <div style={{ marginTop: 'var(--sp-2)' }}>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={itens.length >= 12}
          onClick={() => onChange([...itens, { chave: '', rotulo: '', alvo: { caminho: '' } }])}
        >
          Adicionar item à faixa
        </Button>
      </div>
    </div>
  );
}
